import { useState } from 'react';
import cn from "classnames";
import { Formik, Form } from 'formik';
import { validate, formatMobile } from '../../../../lib/validation';
import Button from '../../../../audi-ui-components/Button';
import FormField from '../../../../components/FormField';
import FileInputField from '../../../../components/FileInputField';
import { gtmPush } from '../../../../lib/gtm';
import {
  FIELD_RADIOS, FIELD_DEALER_SELECT, FIELD_DATEPICKER, FIELD_CHECKBOX
} from '../../../../constants';
import { requestTypes, newOwnerTypes, validationSchema, mapValues } from "./CVOConstants";
import SubmittedBanner from "./SubmittedBanner";
import Summary from "./Summary";
import NextSteps from "./NextSteps";

const ChangeOwnerForm = ({ profile, vehicle, homeState, dealers, cancel }) => {

  const [step, setStep] = useState(1);
  const [serverError, setServerError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submittedData, setSubmittedData] = useState({});

  const handleSubmit = async (values, formikBag) => {
    setServerError(null);
    let data = {
      formName: "changeownership",
      payload: mapValues(values, vehicle, homeState),
    };
    console.log(data);
    let fd = new FormData();
    fd.append("data", JSON.stringify(data));
    if (values.files1?.name) {
      fd.append("files", values.files1);
      if (values.files2?.name) {
        fd.append("files", values.files2);
      }
    }
    try {
      const response = await fetch(`${process.env.RAZZLE_API}/1/form/changeOwnership`, {
        method: 'POST',
        body: fd
      });
      if (response.ok) {
        // success
        formikBag.setSubmitting(false);
        gtmPush("form", "formSubmitSuccess", "changeOwnership", data.payload);
        setSubmitted(true);
        setSubmittedData(values);
      } else {
        // fail
        let contentType = response.headers.get('Content-Type') || '';
        if (contentType?.includes('application/json')) {
          // json response
          const error = await response.json();
          // console.log(error);
          if (error?.modelState) {
            // let errors = mapApiErrors(error.modelState);
            let errors = {};
            for (let key in error.modelState) {
              if (!Object.hasOwnProperty(key)) {
                errors[key] = error.modelState[key][0];
              }
            }
            if (errors.vin) {
              setServerError(errors.vin);
            } else {
              formikBag.setErrors(errors);
            }
            // go to step
            if (errors.phone || errors.email) { setStep(2); }
            else if (errors.newOwnerPhone || errors.newOwnerEmail) { setStep(3); }
            // scroll to first error
            setTimeout(() => {
              let el = document.querySelector(".is-invalid");
              if (el) {
                el.scrollIntoView({behavior: "smooth", block: "center"});
              }
            }, 1000);
          } else {
            var msg = "An error has occured";
            if (error?.message) {
              msg = error.message;
            }
            setServerError(msg);
          }
        } else {
          // text response
          const resultText = await response.text();
          let msg = `Error ${response.status}: ${resultText}`;
          setServerError(msg);
        }
        formikBag.setSubmitting(false);
      }
    } catch (error) {
      // fail
      formikBag.setSubmitting(false);
      setServerError(error.message);
    }
  }

  const validateForm = (values) => {
    var v = validate(values, validationSchema, {format: "firstError", fullMessages: false});
    // console.log(v);
    return v;
  }

  if (submitted) {
    return (<>
    <SubmittedBanner requestType={submittedData?.requestType} />
    <div className="content-wrapper pb-7 cvo">
      <Summary submittedData={submittedData} vehicle={vehicle} homeState={homeState} />
      <NextSteps requestType={submittedData?.requestType} />
    </div>
    </>);
  }

  return (
    <div className="content-wrapper pb-7 cvo">
      <div className="row">
        <div className="col-5 d-none d-medium-block">
          <div className="side-hero" style={{backgroundImage: `url("${process.env.PUBLIC_PATH}assets/cvo-banner.jpg")`}}>&nbsp;</div>
        </div>
        <div className="col-12 col-medium">
          <div className="px-0 px-small-5 px-large-7 py-3 py-medium-5 h-100">
            <Formik
              initialValues={{
                firstName: profile.firstName,
                lastName: profile.lastName,
                email: profile.email,
                phone: profile.mobile,
                address: profile.homeAddress,
                suburb: profile.homeSuburb,
                postCode: profile.homePostcode
              }}
              validate={validateForm}
              onSubmit={handleSubmit}>
              {formikBag => (
                <Form className="audi-form">

                  <h2 className="aui-headline-2 mb-5">Change ownership details</h2>
                  {step === 1 && <>
                    <p className="mb-5">If you&apos;ve disposed of your vehicle, updated your personal details or your Audi has been written off, we&apos;ll take care of you.</p>
                    <p className="mb-7">Complete the form below and we&apos;ll handle the rest.</p>
                  </>}

                  {vehicle?.vin && <div className="vehicle mb-7">
                    <div>
                      <p className="vehicle-heading">Your Audi</p>
                      <p><b>{vehicle.modelYear} {vehicle.modelName}</b></p>
                      <p>{vehicle.regoNo} ({homeState})</p>
                      <p>{vehicle.vin}</p>
                    </div>
                    <div className="vehicle-img">
                      {vehicle?.jellybean && <img src={vehicle.jellybean} alt="" />}
                    </div>
                  </div>}

                  <div className="form-step">
                    <div className={cn({"d-none": step !== 1})}>
                      <p className="aui-headline-4 mb-4">Reason for change</p>
                      <FormField name="requestType"
                        fieldType={FIELD_RADIOS}
                        options={[
                          {label: requestTypes.NO_LONGER_OWN, value: requestTypes.NO_LONGER_OWN},
                          {label: requestTypes.WRITTEN_OFF, value: requestTypes.WRITTEN_OFF}
                        ]}
                        formikBag={formikBag}
                        className="mb-5 stacked"
                      />
                    </div>

                    <div className={cn({"d-none": step !== 2})}>
                      <p className="aui-headline-4 mb-5">Your details</p>
                      <div className="row">
                        <div className="col-small-6">
                          <FormField name="firstName" label="First name" formikBag={formikBag} />
                        </div>
                        <div className="col-small-6">
                          <FormField name="lastName" label="Last name" formikBag={formikBag} />
                        </div>
                      </div>
                      <FormField name="email" label="Email" formikBag={formikBag} />
                      <FormField name="phone" label="Mobile number" formatValue={formatMobile} formikBag={formikBag} />
                      <FormField name="address" label="Address" formikBag={formikBag} />
                      <div className="row">
                        <div className="col-small-8">
                          <FormField name="suburb" label="Suburb" formikBag={formikBag} />
                        </div>
                        <div className="col-small-4">
                          <FormField name="postCode" label="Postcode" formikBag={formikBag} />
                        </div>
                      </div>
                    </div>

                    <div className={cn({"d-none": step !== 3})}>
                      <p className="aui-headline-4 mb-5">Who did you sell this vehicle to?</p>
                      <FormField name="newOwnerType"
                        fieldType={FIELD_RADIOS}
                        options={[
                          {label: newOwnerTypes.PRIVATE, value: newOwnerTypes.PRIVATE},
                          {label: newOwnerTypes.AUDI, value: newOwnerTypes.AUDI},
                          {label: newOwnerTypes.DEALER, value: newOwnerTypes.DEALER},
                        ]}
                        formikBag={formikBag}
                      />
                      {formikBag.values.newOwnerType === newOwnerTypes.PRIVATE && <div>
                        <p>Please ask the new owner to fill in <a href="https://forms.audi.com.au/change-vehicle-ownership" target="_blank" rel="noreferrer" className="aui-textlink">this form</a> and select "I am the new owner of this vehicle"</p>
                      </div>}
                      {formikBag.values.newOwnerType === newOwnerTypes.AUDI && <div>
                        <FormField name="dealerId" label="Dealer" fieldType={FIELD_DEALER_SELECT} dealers={dealers} valueProp="dealer.dealerId" formikBag={formikBag} />
                      </div>}
                    </div>

                    <div className={cn({"d-none": step !== 4})}>
                      <p className="aui-headline-4 mb-5">Sale details</p>
                      <div className="mb-7" style={{maxWidth: "340px"}}>
                        <FormField
                          name="dateOfChange"
                          fieldType={FIELD_DATEPICKER}
                          label={formikBag.values.requestType === requestTypes.WRITTEN_OFF ? "Write-off date (dd/mm/yyyy)" : "Date of sale (dd/mm/yyyy)"}
                          formikBag={formikBag}
                        />
                      </div>
                      {formikBag.values.requestType === requestTypes.WRITTEN_OFF && <div className="pt-3 pb-5">
                        <p className="aui-headline-4 mb-4">Supporting documents</p>
                        <p className="mb-3">
                          A clear, legible photo or scan helps us finalise your request smoothly. If we can&apos;t read the details we&apos;ll need to reach out, which may delay the process.
                        </p>
                        <div>
                          <FileInputField name="files1"
                            label={<><b>Insurance confirmation</b> (required)</>}
                            value={formikBag.values.files1}
                            onChange={(v) => { formikBag.setFieldValue('files1', v); }}
                            isInvalid={formikBag.errors.files1 && formikBag.touched.files1}
                            invalidMessage={formikBag.errors.files1}
                            onBlur={() => { formikBag.setFieldTouched('files1'); }}
                            className="mb-5"
                          />
                          <FileInputField name="files2"
                            label={<><b>Personal Property Security Register</b> (optional)</>}
                            value={formikBag.values.files2}
                            onChange={(v) => { formikBag.setFieldValue('files2', v); }}
                            isInvalid={formikBag.errors.files2 && formikBag.touched.files2}
                            invalidMessage={formikBag.errors.files2}
                            onBlur={() => { formikBag.setFieldTouched('files2'); }}
                            className="mb-5"
                          />
                        </div>
                      </div>}
                      <hr />
                      <FormField name="consent"
                        fieldType={FIELD_CHECKBOX}
                        label="I consent to being contacted by Audi Australia to verify change of ownership details (If you uncheck this box, we may not be able to process your request)"
                        formikBag={formikBag}
                        wrapperClassName="my-7"
                      />
                      <FormField name="optIn"
                        fieldType={FIELD_CHECKBOX}
                        label="I consent to being contacted by Audi Australia and Audi Dealers with occasional communications on products, offers, news and events."
                        formikBag={formikBag}
                        wrapperClassName="my-7"
                      />
                      <p className="mb-2"><b>Data Privacy Statement</b></p>
                      <p className="mb-5">
                        Audi Australia, a division of Volkswagen Group Australia Pty Ltd, will collect, hold and use your personal information for the purpose(s) set out above. Audi may disclose your personal information to Dealers, related companies or third parties that provide us with services, including to overseas locations (such as Germany). If you consent, Audi and Audi Dealers may also use your personal information to send you communications concerning products, offers, news and events. Please note that Audi and Audi Dealers are separate organisations, and that Dealers will handle your information in accordance with their own privacy policies. Our Privacy Policy explains how you can request access to and the correction of your personal information and how you may make a privacy complaint to us. 
                        For full details, see our <a href="http://www.audi.com.au/privacypolicy" target="_blank" rel="noopener noreferrer" className="aui-textlink">Privacy Policy</a>.
                      </p>
                    </div>
                  </div>

                  <div className="form-actions">
                    {serverError && <p className="mb-5 aui-color-text-red server-error">{serverError}</p>}
                    <Button variant="secondary" onClick={cancel} className="me-2">Cancel</Button>
                    {step > 1 && <Button variant="secondary" onClick={() => {
                      if (step === 4 && formikBag.values.requestType !== requestTypes.NO_LONGER_OWN) {
                        setStep(2); // skip New Owner details
                      } else {
                        setStep(step - 1);
                      }
                    }}>Back</Button>}
                    {step < 4 && (
                      <Button
                        variant="primary"
                        onClick={() => {
                          if (step === 1) {
                            formikBag.setFieldTouched("requestType");
                            if (!formikBag.errors.requestType) { setStep(2); }
                          } else if (step === 2) {
                            let fieldKeys = ["firstName", "lastName", "email", "phone", "address", "suburb", "postCode"];
                            let errorKeys = Object.keys(formikBag.errors);
                            let intersection = fieldKeys.filter(x => errorKeys.includes(x)) || [];
                            formikBag.setTouched(Object.fromEntries(fieldKeys.map(k => [k, true])));
                            if (intersection.length === 0) {
                              if (formikBag.values.requestType !== requestTypes.NO_LONGER_OWN) {
                                setStep(4); // skip New Owner details
                              } else {
                                setStep(3);
                              }
                            }
                          } else if (step === 3) {
                            formikBag.setTouched({"newOwnerType": true, "dealerName": true});
                            if (!formikBag.errors.newOwnerType && !formikBag.errors.dealerName) {
                              setStep(4);
                            }
                          }
                        }}
                      >Next</Button>
                    )}
                    {step === 4 && <Button
                      variant="primary"
                      iconPosition="right"
                      type="submit"
                      disabled={formikBag.isSubmitting}
                    >Submit</Button>}
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChangeOwnerForm;
